import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../services/adminApi';
import './AdminOrganizations.css';

function AdminOrganizations() {
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });

    const loadOrganizations = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit
            };
            if (search) params.search = search;
            if (statusFilter !== 'all') params.status = statusFilter;

            const res = await adminApi.getOrganizations(params);
            setOrganizations(res.data.organizations || []);
            setPagination(prev => ({
                ...prev,
                total: res.data.pagination?.total || 0,
                totalPages: res.data.pagination?.total_pages || 0
            }));
        } catch (err) {
            setError('Erreur lors du chargement des organisations');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, statusFilter]);

    useEffect(() => {
        loadOrganizations();
    }, [loadOrganizations]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const getPlanLabel = (plan) => {
        const plans = {
            'free': 'Gratuit',
            'starter': 'Starter',
            'professional': 'Professionnel',
            'enterprise': 'Entreprise'
        };
        return plans[plan] || plan;
    };

    return (
        <AdminLayout>
            <div className="admin-organizations">
                <div className="admin-page-header">
                    <div>
                        <h1>Organisations</h1>
                        <p>Gestion des organisations inscrites sur la plateforme</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="admin-filters">
                    <div className="admin-search">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Rechercher une organisation..."
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>
                    <select
                        className="admin-select"
                        value={statusFilter}
                        onChange={handleStatusFilter}
                    >
                        <option value="all">Tous les statuts</option>
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                    </select>
                    <span className="admin-result-count">
                        {pagination.total} organisation{pagination.total > 1 ? 's' : ''}
                    </span>
                </div>

                {/* Table */}
                <div className="admin-card">
                    {loading ? (
                        <div className="admin-loading-inline">Chargement...</div>
                    ) : error ? (
                        <div className="admin-error">{error}</div>
                    ) : organizations.length === 0 ? (
                        <p className="no-data">Aucune organisation trouvee</p>
                    ) : (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Organisation</th>
                                    <th>Plan</th>
                                    <th>Utilisateurs</th>
                                    <th>Signatures</th>
                                    <th>Statut</th>
                                    <th>Cree le</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {organizations.map(org => (
                                    <tr key={org.id}>
                                        <td>
                                            <div className="org-info">
                                                <Link to={`/admin/organizations/${org.id}`} className="org-name">
                                                    {org.name}
                                                </Link>
                                                {org.billing_email && (
                                                    <span className="org-email">{org.billing_email}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`plan-badge ${org.subscription_plan || 'free'}`}>
                                                {getPlanLabel(org.subscription_plan)}
                                            </span>
                                        </td>
                                        <td>{org.user_count || 0}</td>
                                        <td>{org.signature_count || 0}</td>
                                        <td>
                                            <span className={`admin-status ${org.is_active ? 'active' : 'inactive'}`}>
                                                {org.is_active ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td>{formatDate(org.created_at)}</td>
                                        <td>
                                            <Link
                                                to={`/admin/organizations/${org.id}`}
                                                className="admin-btn admin-btn-secondary admin-btn-sm"
                                            >
                                                Voir
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="admin-pagination">
                            <button
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                            >
                                Precedent
                            </button>
                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                let pageNum;
                                if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (pagination.page <= 3) {
                                    pageNum = i + 1;
                                } else if (pagination.page >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                } else {
                                    pageNum = pagination.page - 2 + i;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={pagination.page === pageNum ? 'active' : ''}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            <button
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.totalPages}
                            >
                                Suivant
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminOrganizations;
